import { useState, useEffect } from 'react'
import { getNavigationPath } from '../getNavigationPath'
import { NewsIcon, NavigatorIcon, HerdPowerIcon, DecoderIcon } from 'assets/icons/icons'

// Typesense configuration
const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST
const TYPESENSE_PORT = '443'
const TYPESENSE_PROTOCOL = 'https'
const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY

interface TypesenseDocument {
	aliases: string[]
	company: string
	decoder: number
	herd: number
	id: string
	lo1: string
	news: number
	page_type:
		| 'other'
		| 'summary'
		| 'earnings'
		| 'metrics'
		| 'dynamics'
		| 'statements'
		| 'herdpower'
		| 'news'
		| 'feature'
		| 'stock'
		| 'etf'

	search_blob: string
	sector: string
	ticker: string
	ticker_id: number
}

interface TypesenseResponse {
	facet_counts: any[]
	found: number
	hits: { document: TypesenseDocument }[]
}

export const useStockOptions = (ticker: string | null, pageType?: string) => {
	const [stockOptions, setStockOptions] = useState<any[]>([])

	useEffect(() => {
		if (!ticker) {
			setStockOptions([])
			return
		}

		// Fetch from Typesense
		const url = `${TYPESENSE_PROTOCOL}://${TYPESENSE_HOST}:${TYPESENSE_PORT}/collections/mavbots_helix/documents/search`
		const params = new URLSearchParams({
			q: ticker,
			query_by: 'ticker,page_type',
			filter_by: 'page_type:!=stock && page_type:!=etf',
			per_page: '50',
			sort_by: '_text_match:desc, priority:asc'
		})

		// Add page_type filter if provided
		if (pageType) {
			params.set('filter_by', `page_type:${pageType}`)
		}

		fetch(`${url}?${params.toString()}`, {
			headers: {
				'X-TYPESENSE-API-KEY': TYPESENSE_API_KEY || '',
				'Content-Type': 'application/json'
			}
		})
			.then(async res => {
				if (!res.ok) throw new Error(`Typesense error: ${res.status}`)
				return res.json() as Promise<TypesenseResponse>
			})
			.then(data => {
				// Transform Typesense hits into stock options
				const options = data.hits
					.filter(hit => hit.document && hit.document.page_type) // Filter out any documents without page_type
					.map(hit => {
						const doc = hit.document
						const pageType = doc.page_type
						const label = pageType.charAt(0).toUpperCase() + pageType.slice(1)

						// Determine icon based on page_type
						let icon = undefined
						if (['other', 'summary', 'earnings', 'metrics', 'dynamics', 'statements'].includes(pageType)) {
							icon = DecoderIcon
						} else if (pageType === 'news') {
							icon = NewsIcon
						} else if (pageType === 'herdpower') {
							icon = HerdPowerIcon
						} else if (pageType.includes('navigator')) {
							icon = NavigatorIcon
						}

						return {
							label,
							path: getNavigationPath(pageType, doc.ticker, doc.company),
							type: 'Feature',
							icon,
							keywords: doc.aliases || []
						}
					})
				setStockOptions(options)
			})
			.catch(err => {
				console.error('Failed to fetch stock options:', err)
				setStockOptions([])
			})
	}, [ticker, pageType])

	return stockOptions
}
